import React, {Component, RefObject} from 'react';
import {KatInput} from "@amzn/katal-react";
import numeral from "numeral";
import 'babel-polyfill';
import FormatUtil from "src/utils/FormatUtil";

/**
 * Based on https://code.amazon.com/packages/SkuCentralUICommon/blobs/mainline/--/src/components/SkuCentralNumberInput.tsx
 */
export type inputValidation = (input: string) => ValidationResult;

export interface ValidationResult {
    isValid: boolean,
    errorMsg: string
}

interface Props {
    readonly isDisabled: boolean;
    readonly isFloat: boolean;
    readonly isMandatory?: boolean;
    readonly defaultErrorMsg?: string;
    readonly maxValue?: number;
    readonly minValue?: number;
    readonly customValidation?: inputValidation
    readonly onInput?: (input: number, hasError: boolean) => void;
    readonly onChange?: (input: number, hasError: boolean) => void;
    readonly value?: number;
    readonly className?: string;
    readonly nextRef?: RefObject<any>;
    readonly triggerInputEventForDefaultValue?: boolean;
}

interface State {
    readonly hasError: boolean;
    readonly errorInfo?: string;
}

export default class NumberKatInput extends Component<Props, State> {
    private static readonly integerRegex: RegExp = /^\d*$/;
    private static readonly floatRegex: RegExp = /^\d*[.,]?\d*$/;

    private readonly inputRef: RefObject<any>;

    constructor(props: Props) {
        super(props);

        let displayValue = '';
        let validateResult = {isValid: true, errorMsg: ''};

        if (this.props.value != undefined) {
            let num = numeral(this.props.value);
            // @ts-ignore
            displayValue = num.value() != null ? num.value().toString() : '';

            validateResult = this.validateInput(displayValue);

            // trigger inputEvent for when the component first load
            if ((this.props.triggerInputEventForDefaultValue ?? true) && this.props.onInput) {
                this.props.onInput(num.value() as number, !validateResult.isValid);
            }
        }

        this.inputRef = React.createRef();
        this.handleInputEvent = this.handleInputEvent.bind(this);
        this.handleChangeEvent = this.handleChangeEvent.bind(this);
        this.handleKeyupEvent = this.handleKeyupEvent.bind(this);

        this.state = {
            hasError: !validateResult.isValid,
            errorInfo: validateResult.errorMsg
        };
    }

    private validateInput(inputString: string): ValidationResult {
        const defaultErrorMsg: string = this.props.defaultErrorMsg ? this.props.defaultErrorMsg : '';

        if ((this.props.isMandatory && this.isInputEmpty(inputString))
            || this.isOutOfBoundary(inputString) ||
            !this.isMatchPattern(inputString)) {
            return {isValid: false, errorMsg: defaultErrorMsg};
        }

        if (this.props.customValidation) {
            const result = this.props.customValidation(inputString);

            if (!result.isValid) {
                return {isValid: false, errorMsg: result.errorMsg ? result.errorMsg : defaultErrorMsg}
            }
        }

        return {isValid: true, errorMsg: ''}
    }

    private isInputEmpty(inputString: string): boolean {
        return inputString == null || inputString === '';
    }

    private isOutOfBoundary(inputString: string): boolean {
        const {maxValue, minValue} = this.props;

        let inputNumber = numeral(inputString).value();
        let isValid: boolean = true;

        if (minValue) {
            // @ts-ignore
            isValid = isValid && inputNumber >= minValue;
        }

        if (maxValue) {
            // @ts-ignore
            isValid = isValid && inputNumber <= maxValue;
        }

        return !isValid;
    }

    private isMatchPattern(inputString: string): boolean {
        const regex: RegExp = (this.props.isFloat) ? NumberKatInput.floatRegex : NumberKatInput.integerRegex;

        return regex.test(inputString);
    }

    private handleInputEvent(event: KatInput.InputEvent) {
        this.handleInput(event.currentTarget.value, this.props.onInput);
    }

    private handleChangeEvent(event: KatInput.ChangeEvent) {
        this.handleInput(event.currentTarget.value, this.props.onChange);
    }

    private handleKeyupEvent(event: KatInput.KeyupEvent) {
        let key = (event as unknown as KeyboardEvent).key;
        if (key === "Enter" && this.props.nextRef) {
            let nextRef = this.props.nextRef as RefObject<NumberKatInput>;
            nextRef.current?.inputRef.current.focus();
            event.preventDefault();
        }
    }

    private handleInput(input: string, callback?: Function) {
        let inputForVal = FormatUtil.formatInput(input);
        let num = numeral(inputForVal);
        // @ts-ignore
        const displayValue = num.value() != null ? num.value().toString() : '';
        const validationResult = this.validateInput(displayValue);

        this.setState({
            hasError: !validationResult.isValid,
            errorInfo: validationResult.errorMsg
        });

        if (callback) {
            // @ts-ignore
            const val: number = num.value() != null ? num.value() : 0;
            callback(val, this.state.hasError);
        }
    }

    public render() {
        const buildInputElement = () => {
            return (
                <KatInput
                    disabled={this.props.isDisabled}
                    type='text'
                    state={this.state.hasError ? 'error' : undefined}
                    stateLabel={this.state.errorInfo}
                    value={FormatUtil.formatNumberDisplay(this.props.value)}
                    onInput={this.handleInputEvent}
                    onChange={this.handleChangeEvent}
                    onKeyup={this.handleKeyupEvent}
                    autofocus
                    className={this.props.className}
                    ref={this.inputRef}
                />
            );
        };

        return buildInputElement();
    }
}